import { useMemo } from 'react'
import { type AgentRunsListItem, formatCost, MATCH_SIDE_LABELS } from '~/entities/document-agent'
import { Text } from '~/shared/ui'
import { getRunsPerformance, type Rate } from '../lib'

function formatRate(rate: Rate): string {
  return rate.n ? `${Math.round((rate.correct / rate.n) * 100)}%` : '--'
}

function StatTile({ label, value, caption }: { label: string; value: string; caption: string }) {
  return (
    <div className="border-divider bg-surface-lowest flex min-w-[170px] flex-col gap-0.5 rounded-lg border px-4 py-3">
      <Text
        className="text-text-secondary"
        appearance="CAPTION"
      >
        {label}
      </Text>
      <span className="text-text-primary text-[20px] leading-7 font-semibold">{value}</span>
      <Text
        className="text-text-secondary"
        appearance="CAPTION"
      >
        {caption}
      </Text>
    </div>
  )
}

export function AgentRunsPerformance({ items }: { items: AgentRunsListItem[] }) {
  const performance = useMemo(() => getRunsPerformance(items), [items])
  const statuses = Object.entries(performance.matchStatuses).sort(([, a], [, b]) => b - a)
  const matched = performance.matchStatuses.completed ?? 0
  const withMatching = statuses.reduce((sum, [, count]) => sum + count, 0)

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-3">
        <StatTile
          label="Category agrees with final"
          value={formatRate(performance.category)}
          caption={`${performance.category.correct} of ${performance.category.n} reviewed`}
        />
        {(['claim', 'pa'] as const).map((side) => {
          const { all, ruleAbstained } = performance.sides[side]
          return (
            <StatTile
              key={side}
              label={`${MATCH_SIDE_LABELS[side]} pick agrees with final`}
              value={formatRate(all)}
              caption={`${all.correct} of ${all.n} · rule abstained ${formatRate(ruleAbstained)} of ${ruleAbstained.n}`}
            />
          )
        })}
        <StatTile
          label="Matching completed"
          value={`${matched} of ${withMatching}`}
          caption={
            statuses
              .filter(([status]) => status !== 'completed')
              .map(([status, count]) => `${status} ${count}`)
              .join(' · ') || 'No other status'
          }
        />
        <StatTile
          label="Mean cost per run"
          value={formatCost(performance.meanCost.total)}
          caption={`classification ${formatCost(performance.meanCost.classification)} · matching ${formatCost(performance.meanCost.matching)}`}
        />
      </div>
      <Text
        className="text-text-tertiary"
        appearance="CAPTION"
      >
        {`Over the newest run of the ${performance.documents} documents loaded below. Picks are scored on the document's final side against the reviewer's final case; a final case in the agent's tie counts as not agreeing.`}
      </Text>
    </div>
  )
}
